import { HealthSystemDomainUsersResultBusinessUnitResponse } from '../api/HealthSystemApi';

/**
 * The claims the backend puts on an access token, limited to the ones this app actually reads.
 *
 * Identity-dependent claims are optional on purpose: a veterinary's token carries no `farmId`
 * (nor `farmNumber` / `farmType`) and a farm user's token carries no `vetId` / `vetNumber`, so
 * the auth state built from them is honestly `string | undefined`. Only `exp`, `name` and `role`
 * are issued for every token.
 */
export interface JwtClaims {
  exp: number;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
  userId?: string;
  locale?: string;
  farmId?: string;
  farmNumber?: string;
  farmType?: string;
  vetId?: string;
  vetNumber?: string;
  unitId?: string;
  culture?: string;
}

export function parseJwt(token: string): JwtClaims {
  const base64Url = token.split('.')[1];

  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export function validateJwtToken(token: string): boolean {
  const jwt = parseJwt(token);
  return validateJwtExp(jwt.exp);
}

export function validateJwtExp(exp: number) {
  const current_time = new Date().getTime() / 1000;

  if (current_time > exp) {
    return false;
  }

  return true;
}

export function getAuthValueFromToken(token: string) {
  const parsedToken = parseJwt(token);
  // The token does not carry them; useAuthContext fills them in from the stored business units.
  const businessUnits: HealthSystemDomainUsersResultBusinessUnitResponse[] = [];

  return {
    isLoggedIn: true,
    isValid: validateJwtExp(parsedToken.exp),
    token,
    userName: parsedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
    role: parsedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
    userId: parsedToken['userId'],
    locale: parsedToken['locale'],
    farmId: parsedToken['farmId'],
    farmNumber: parsedToken['farmNumber'],
    farmName: '',
    farmType: Number(parsedToken['farmType']),
    farmMetadata: null as {
      temperatureUnit?: number;
      weightUnit?: number;
    } | null,
    vetId: parsedToken['vetId'],
    vetNumber: parsedToken['vetNumber'],
    selectedBusinessUnitId: parsedToken['unitId'],
    culture: parsedToken['culture'] || 'en-NL',
    businessUnits,
    farmTimeZoneId: ''
  };
}
